import React from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { IonicButton, IonicInput } from 'ionic-ui';
import Layout from './Layout';
import { z } from 'zod';
import { emailSchema, phoneSchema } from '../../schemas/validation';
import { baseURL } from '../../data/config';
import { request } from '../../utils/request';
import useIonicToast from '../../hooks/useIonicToast';
import { usePageLoader } from '../../context/pageLoader';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const recoveryModeEnum = z.enum(['email', 'phone']);
const deliveryViaEnum = z.enum(['email', 'sms', 'whatsapp']);

const normalizePhoneInput = (input: string): string => {
  const cleaned = input.replace(/[^\d+]/g, '');

  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('0')) return `+234${cleaned.slice(1)}`;
  if (cleaned.startsWith('234')) return `+${cleaned}`;
  if (cleaned.length >= 10 && cleaned.length <= 15) return `+234${cleaned}`;

  return cleaned;
};

const FormSchema = z
  .object({
    mode: recoveryModeEnum,
    identifier: z.string().min(1, { message: 'Identifier is required' }),
    via: deliveryViaEnum,
  })
  .superRefine((value, ctx) => {
    if (value.mode === 'email') {
      if (!emailSchema.safeParse(value.identifier).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identifier'],
          message: 'Enter a valid email address',
        });
      }

      if (value.via !== 'email') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['via'],
          message: 'Email recovery must use email delivery',
        });
      }
      return;
    }

    const normalizedPhone = normalizePhoneInput(value.identifier);
    if (!phoneSchema.safeParse(normalizedPhone).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identifier'],
        message: 'Enter a valid phone number',
      });
    }
  });

const ForgotPassword: React.FC = () => {
  const { presentIonToast } = useIonicToast();
  const history = useHistory();
  const { setBusy } = usePageLoader();

  const formMethod = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      mode: 'email',
      identifier: '',
      via: 'email',
    },
  });

  const mode = formMethod.watch('mode');

  React.useEffect(() => {
    if (mode === 'email' && formMethod.getValues('via') !== 'email') {
      formMethod.setValue('via', 'email');
    }

    if (mode === 'phone' && !formMethod.getValues('identifier')) {
      formMethod.setValue('identifier', '+234');
    }
  }, [mode]);

  // Handle form submission
  const handleSubmit = formMethod.handleSubmit(
    async ({ mode, identifier, via }) => {
      setBusy(true);

      const trimmedIdentifier = identifier.trim();
      const normalizedIdentifier =
        mode === 'email'
          ? trimmedIdentifier.toLowerCase()
          : normalizePhoneInput(trimmedIdentifier);

      const requestPayload: Record<string, string> = {
        via: mode === 'email' ? 'email' : via,
      };

      if (mode === 'email') requestPayload.email = normalizedIdentifier;
      else requestPayload.phone = normalizedIdentifier;

      await request({
        method: 'POST',
        url: baseURL + '/auth/request/forgot-password',
        formData: requestPayload,
        callback: () => {
          history.replace({
            pathname: '/auth/reset-password',
            state: {
              identifier: normalizedIdentifier,
              mode,
              via: mode === 'email' ? 'email' : via,
            },
          });
        },
        onError: (err: any) => {
          console.log(err);

          let msg: string = err.message || 'Something went wrong, try again';
          if (err.status === 401) {
            msg = 'User not found';
          }

          if (err.status === 429) {
            msg = 'Too many requests. Please try again in 1 hour.';
          }

          presentIonToast(msg, 'error');
        },
      });

      setBusy(false);
    }
  );

  return (
    <Layout
      uiType="signin"
      title="Forgot Password"
      description="Use your email or phone number to receive a reset OTP."
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-sm">
        <Controller
          name="mode"
          control={formMethod.control}
          render={({ field }) => (
            <IonicInput
              size="xlg"
              type="select"
              label="Reset with"
              aria-label="Reset with"
              {...field}
              options={[
                { label: 'Email', value: 'email' },
                { label: 'Phone', value: 'phone' },
              ]}
            />
          )}
        />

        <Controller
          name="identifier"
          control={formMethod.control}
          render={({ field, fieldState }) => (
            <IonicInput
              size="xlg"
              type={mode === 'email' ? 'email' : 'tel'}
              label={mode === 'email' ? 'Email' : 'Phone'}
              aria-label={mode === 'email' ? 'Email' : 'Phone'}
              placeholder={
                mode === 'email' ? 'Enter your email' : '+2348012345678'
              }
              {...field}
              hint={fieldState.error?.message}
              hintClass="text-red-600 dark:text-red-600"
            />
          )}
        />

        {mode === 'phone' && (
          <Controller
            name="via"
            control={formMethod.control}
            render={({ field, fieldState }) => (
              <IonicInput
                size="xlg"
                type="select"
                label="Send OTP via"
                aria-label="Send OTP via"
                {...field}
                options={[
                  { label: 'SMS', value: 'sms' },
                  { label: 'WhatsApp', value: 'whatsapp' },
                ]}
                hint={fieldState.error?.message}
                hintClass="text-red-600 dark:text-red-600"
              />
            )}
          />
        )}

        {/** Remember... & Forgot password */}
        <div className="flex items-center justify-end ">
          <Link
            to="/auth/sign-in"
            className="text-primary text-sm font-medium font-dm_sans"
          >
            Remembered password?
          </Link>
        </div>

        {/* Login Button */}
        <IonicButton type="submit" size="xlg" className="w-full">
          Continue
        </IonicButton>

        <p className="text-center text-text-dark-medium dark:text-text-light-medium mt-4">
          Don't have an account?{' '}
          <Link to="/auth/sign-up" className="text-primary font-semibold">
            Sign up
          </Link>
        </p>
      </form>
    </Layout>
  );
};

export default ForgotPassword;
